import { useState, useCallback, useEffect, useRef } from 'react';
import * as L from 'leaflet';
import { Place, PlaceContext, PlaceSearchParams } from '@m4a3/api-client';
import { orderfulApi } from '../../../services/api-instances';
import { normalizeDropPoints } from '../../../utils/cellDropPointUtils';
import { logToDatadog } from '../../../services/datadog';
import { StatusType } from '@datadog/browser-logs';
import { MAP_CONFIG } from '../constants/mapConfig';

// Simple in-memory cache without hooks
const placesCache = new Map<string, { data: any; expires: number }>();
const contextCache = new Map<string, { data: any; expires: number }>();
const pendingRequests = new Map<string, Promise<any>>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const usePlaceData = () => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [placeStatuses, setPlaceStatuses] = useState<Map<string, { enabled: boolean; name?: string }>>(new Map());
  const [loading, setLoading] = useState(false);
  
  const loadingRef = useRef(false);
  const nextLoadRef = useRef<{ center: L.LatLng; zoom: number } | null>(null);

  // Cleanup cache periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      // Clean expired places cache
      for (const [key, entry] of placesCache.entries()) {
        if (now >= entry.expires) {
          placesCache.delete(key);
        }
      }
      // Clean expired context cache
      for (const [key, entry] of contextCache.entries()) {
        if (now >= entry.expires) {
          contextCache.delete(key);
        }
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  
  // Simple cache functions without useCallback dependencies
  const getCachedPlaces = (params: PlaceSearchParams) => {
    const key = `places-${params.latitude.toFixed(4)}-${params.longitude.toFixed(4)}-${params.radiusMeters}`;
    const cached = placesCache.get(key);
    if (cached && Date.now() < cached.expires) {
      return cached.data;
    }
    if (cached) {
      placesCache.delete(key);
    }
    return null;
  };

  const cachePlaces = (params: PlaceSearchParams, data: any) => {
    const key = `places-${params.latitude.toFixed(4)}-${params.longitude.toFixed(4)}-${params.radiusMeters}`;
    placesCache.set(key, {
      data,
      expires: Date.now() + CACHE_DURATION
    });
  };

  const getCachedContext = (placeId: string) => {
    const cached = contextCache.get(placeId);
    if (cached && Date.now() < cached.expires) {
      return cached.data;
    }
    if (cached) {
      contextCache.delete(placeId);
    }
    return null;
  };

  const cacheContext = (placeId: string, data: any) => {
    contextCache.set(placeId, {
      data,
      expires: Date.now() + CACHE_DURATION
    });
  };

  // FIXED: Load places with proper caching and no infinite loops
  const loadPlacesInViewport = useCallback(async (center: L.LatLng, zoom: number) => {
    
    // Prevent multiple simultaneous calls
    if (loadingRef.current) {
      // Queue the latest request; it will run after the in-flight one completes
      nextLoadRef.current = { center, zoom };
      return;
    }

    if (zoom < MAP_CONFIG.MIN_ZOOM_FOR_PLACES) {
      return; // Don't load places at low zoom levels for performance
    }
    
    const params: PlaceSearchParams = {
      latitude: center.lat,
      longitude: center.lng,
      radiusMeters: MAP_CONFIG.ZOOM_RADIUS_MAP[zoom as keyof typeof MAP_CONFIG.ZOOM_RADIUS_MAP] || 1000,
      limit: 500,
    };

    const cacheKey = `places-${params.latitude.toFixed(4)}-${params.longitude.toFixed(4)}-${params.radiusMeters}`;

    // Check cache first
    const cachedResponse = getCachedPlaces(params);
    if (cachedResponse) {
      // Ensure new array reference for rendering
      setPlaces([...(cachedResponse.places || [])]);
      
      // Cache status information for all places immediately for color coding
      const newStatuses = new Map();
      cachedResponse.places.forEach((place: Place) => {
        newStatuses.set(place.id, {
          enabled: place.enabled,
          name: place.friendlyName
        });
      });
      setPlaceStatuses(prev => new Map([...prev, ...newStatuses]));
      
      logToDatadog('places_loaded_from_cache', { 
        center: { lat: center.lat, lng: center.lng }, 
        zoom, 
        count: cachedResponse.places.length 
      });
      return;
    }

    // Check for pending request (deduplication)
    const pendingRequest = pendingRequests.get(cacheKey);
    if (pendingRequest) {
      try {
        const response = await pendingRequest;
        // Ensure new array reference for rendering
        setPlaces([...(response.places || [])]);
        
        // Cache status information
        const newStatuses = new Map();
        response.places.forEach((place: Place) => {
          newStatuses.set(place.id, {
            enabled: place.enabled,
            name: place.friendlyName
          });
        });
        setPlaceStatuses(prev => new Map([...prev, ...newStatuses]));
        
        logToDatadog('places_loaded_from_pending', { 
          center: { lat: center.lat, lng: center.lng }, 
          zoom, 
          count: response.places.length 
        });
      } catch (err) {
        console.error('Error loading places from pending request:', err);
        logToDatadog('places_load_failed', { error: err }, StatusType.error);
      }
      return;
    }
    
    try {
      loadingRef.current = true;
      setLoading(true);
      
      // Create new request with caller-side defaults (radius_meters=1000, limit=100, offset=0)
      const paramsWithDefaults: PlaceSearchParams = {
        latitude: params.latitude,
        longitude: params.longitude,
        radiusMeters: params.radiusMeters ?? 1000,
        limit: params.limit ?? 100,
        offset: params.offset ?? 0,
      };
      const requestPromise = orderfulApi.searchPlaces(paramsWithDefaults);
      
      
      // Register as pending for deduplication
      pendingRequests.set(cacheKey, requestPromise);
      
      const response = await requestPromise;
      
      
      // Cache the response
      cachePlaces(params, response);
      
      // Ensure new array reference for rendering
      setPlaces([...(response.places || [])]);
      
      // Cache status information for all places immediately for color coding
      const newStatuses = new Map();
      response.places.forEach(place => {
        newStatuses.set(place.id, {
          enabled: place.enabled,
          name: place.friendlyName
        });
      });
      setPlaceStatuses(prev => new Map([...prev, ...newStatuses]));
      
      logToDatadog('places_loaded_from_api', { 
        center: { lat: center.lat, lng: center.lng }, 
        zoom, 
        count: response.places.length 
      });
    } catch (err) {
      console.error('Error loading places:', err);
      logToDatadog('places_load_failed', { error: err }, StatusType.error);
      // Don't throw error to prevent cascading failures
    } finally {
      loadingRef.current = false;
      setLoading(false);
      pendingRequests.delete(cacheKey);

      // If a newer load was requested while we were loading, run it now (single-shot)
      const next = nextLoadRef.current;
      if (next) {
        nextLoadRef.current = null;
        // Defer to next tick to avoid deep recursion and allow state to flush
        setTimeout(() => {
          void loadPlacesInViewport(next.center, next.zoom);
        }, 0);
      }
    }
  }, []); // Empty deps array is safe since we don't use external refs

  // FIXED: Load place context data with caching and deduplication
  const loadPlaceContext = useCallback(async (place: Place): Promise<PlaceContext> => {
    // Check cache first
    const cachedContext = getCachedContext(place.id);
    if (cachedContext) {
      logToDatadog('place_context_loaded_from_cache', { 
        placeId: place.id,
        dropPointCount: normalizeDropPoints(cachedContext.dropPoints).length
      });
      return cachedContext;
    }

    // Check for pending request
    const pendingRequest = pendingRequests.get(`context-${place.id}`);
    if (pendingRequest) {
      try {
        const context = await pendingRequest;
        logToDatadog('place_context_loaded_from_pending', { 
          placeId: place.id,
          dropPointCount: normalizeDropPoints(context.dropPoints).length
        });
        return context;
      } catch (err) {
        console.error('Error loading place context from pending request:', err);
        logToDatadog('place_context_load_failed', { placeId: place.id, error: err }, StatusType.error);
        throw new Error('Failed to load place details');
      }
    }

    try {
      setLoading(true);
      
      // Create new request
      const requestPromise = orderfulApi.getPlaceContext(place.id);
      
      // Register as pending for deduplication
      pendingRequests.set(`context-${place.id}`, requestPromise);
      
      const context = await requestPromise;
      
      // Cache the response
      cacheContext(place.id, context);
      
      logToDatadog('place_context_loaded_from_api', { 
        placeId: place.id,
        dropPointCount: normalizeDropPoints(context.dropPoints).length
      });

      return context;
    } catch (err) {
      console.error('Error loading place context:', err);
      logToDatadog('place_context_load_failed', { placeId: place.id, error: err }, StatusType.error);
      throw new Error('Failed to load place details');
    } finally {
      setLoading(false);
      pendingRequests.delete(`context-${place.id}`);
    }
  }, []); // Empty deps array is safe

  // FIXED: Refresh place context by ID with cache invalidation
  const refreshPlaceContext = useCallback(async (placeId: string): Promise<PlaceContext> => {
    try {
      // Force refresh by bypassing cache
      contextCache.delete(placeId);
      const requestPromise = orderfulApi.getPlaceContext(placeId);
      const context = await requestPromise;
      
      // Update cache with fresh data
      cacheContext(placeId, context);
      
      logToDatadog('place_context_refreshed', { placeId });
      return context;
    } catch (err) {
      logToDatadog('context_refresh_failed', { placeId, error: err }, StatusType.error);
      throw err;
    }
  }, []); // Empty deps array is safe

  // Get cache statistics for debugging
  const getCacheStats = useCallback(() => {
    return {
      places: {
        total: placesCache.size,
        valid: Array.from(placesCache.values()).filter(entry => Date.now() < entry.expires).length,
        pending: 0,
      },
      context: {
        total: contextCache.size,
        valid: Array.from(contextCache.values()).filter(entry => Date.now() < entry.expires).length,
        pending: 0,
      },
    };
  }, []);

  // Clear cache manually if needed
  const clearCache = useCallback(() => {
    placesCache.clear();
    contextCache.clear();
    pendingRequests.clear();
  }, []);

  // Update place status (enable/disable)
  const updatePlaceStatus = useCallback(async (placeId: string, enabled: boolean): Promise<void> => {
    try {
      setLoading(true);
      
      // Call the API to update place status
      await orderfulApi.updatePlaceContext(placeId, { enabled });
      
      // Update local state
      setPlaceStatuses(prev => {
        const newStatuses = new Map(prev);
        const existingStatus = newStatuses.get(placeId) || {};
        newStatuses.set(placeId, { ...existingStatus, enabled });
        return newStatuses;
      });
      
      // Invalidate cache for this place to force refresh
      contextCache.delete(placeId);
      
      logToDatadog('place_status_updated', { 
        placeId, 
        enabled,
        action: enabled ? 'enabled' : 'disabled'
      });
      
    } catch (err) {
      console.error('Error updating place status:', err);
      logToDatadog('place_status_update_failed', { placeId, enabled, error: err }, StatusType.error);
      throw new Error(`Failed to ${enabled ? 'enable' : 'disable'} place`);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    places,
    placeStatuses,
    loading,
    
    // Actions
    loadPlacesInViewport,
    loadPlaceContext,
    refreshPlaceContext,
    updatePlaceStatus,
    
    // Cache utilities
    getCacheStats,
    clearCache,
    
    // Direct setters (for edge cases)
    setPlaces,
    setPlaceStatuses,
  };
};
